import {type Descendant} from 'slate';
import {create} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';

import {safeLocalStorage} from '../../util/localStorage';
import {
  COMPOSER_INITIAL_VALUE,
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
} from './constants';

const wbAgentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: name => safeLocalStorage.getItem(name),
  setItem: (name, value) => {
    try {
      safeLocalStorage.setItem(name, value);
    } catch (e) {
      if (
        e instanceof DOMException &&
        (e.code === 22 || e.name === 'QuotaExceededError')
      ) {
        console.warn(`localStorage quota exceeded for "${name}"`, e);
        return;
      }
      console.warn(`Failed to persist "${name}" to localStorage`, e);
    }
  },
  removeItem: name => safeLocalStorage.removeItem(name),
};

export interface SelectedRun {
  name: string;
  displayName: string;
}

interface WindowPosition {
  x: number;
  y: number;
}

interface WBAgentContextState {
  // Agent chat open state
  open: boolean;
  // Docked state
  docked: boolean;
  // Drawer width
  drawerWidth: number;
  // Window geometry (persists across open/close and dock/undock)
  windowPosition: WindowPosition | null;
  windowWidth: number;
  windowHeight: number;
  // Composer contents
  composerDraft: Descendant[];
  // Run context
  selectedRuns: SelectedRun[];
  projectName: string | undefined;

  // Actions
  setOpen: (open: boolean) => void;
  setDocked: (docked: boolean) => void;
  setDrawerWidth: (width: number) => void;
  setWindowPosition: (pos: WindowPosition) => void;
  setWindowSize: (width: number, height: number) => void;
  setComposerDraft: (value: Descendant[]) => void;
  addRun: (run: SelectedRun) => void;
  removeRun: (name: string) => void;
  clearRuns: () => void;
  syncProject: (projectName: string | undefined) => void;
}

/* eslint-disable wandb/zustand-enforce-slices-when-large-state -- store pre-dates rule; slicing tracked in follow-up */
export const useWBAgentContextStore = create<WBAgentContextState>()(
  persist(
    set => ({
      open: false,
      docked: true,
      drawerWidth: DEFAULT_DRAWER_WIDTH,
      windowPosition: null,
      windowWidth: DEFAULT_WINDOW_WIDTH,
      windowHeight: DEFAULT_WINDOW_HEIGHT,
      composerDraft: COMPOSER_INITIAL_VALUE,
      selectedRuns: [],
      projectName: undefined,

      setOpen: open => set({open}),
      setDocked: docked => set({docked}),
      setDrawerWidth: width => set({drawerWidth: width}),
      setWindowPosition: pos => set({windowPosition: pos}),
      setWindowSize: (width, height) =>
        set({windowWidth: width, windowHeight: height}),
      setComposerDraft: value => set({composerDraft: value}),
      addRun: run =>
        set(state => {
          if (state.selectedRuns.some(r => r.name === run.name)) {
            return state;
          }
          return {selectedRuns: [...state.selectedRuns, run]};
        }),
      removeRun: name =>
        set(state => ({
          selectedRuns: state.selectedRuns.filter(r => r.name !== name),
        })),
      clearRuns: () => set({selectedRuns: []}),
      syncProject: projectName =>
        set(state => {
          if (state.projectName === projectName) {
            return state;
          }
          return {projectName, selectedRuns: []};
        }),
    }),
    {
      name: 'wb-agent-window-preferences',
      storage: createJSONStorage(() => wbAgentStorage),
      partialize: state => ({
        open: state.open,
        docked: state.docked,
        drawerWidth: state.drawerWidth,
        windowPosition: state.windowPosition,
        windowWidth: state.windowWidth,
        windowHeight: state.windowHeight,
      }),
    }
  )
);
